import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { AuthService } from "../lib/auth";
import {
  isGuestPassUser,
  startRecommendedStudentBriefing,
} from "../lib/student-task-briefing";
import type { UserProfile } from "../types";

const COLORS = {
  bg: "#FAFAF7",
  ink: "#18181B",
  muted: "#71717A",
};

export function GuestDirectBriefingGate({ children }: { children: ReactNode }) {
  const [, setLocation] = useLocation();
  const [error, setError] = useState("");
  const { data: currentUser, isLoading } = useQuery<UserProfile | null>({
    queryKey: ["currentUser"],
    queryFn: () => AuthService.getInstance().getCurrentUser(),
    staleTime: 5 * 60 * 1000,
  });
  const isGuest = isGuestPassUser(currentUser);

  useEffect(() => {
    if (isLoading || !isGuest) return;
    let cancelled = false;
    startRecommendedStudentBriefing({ hideBack: true })
      .then((path) => {
        if (!cancelled) setLocation(path);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "課題を開始できませんでした。");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isGuest, isLoading, setLocation]);

  if (!isLoading && !isGuest) {
    return <>{children}</>;
  }

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ background: COLORS.bg }}
    >
      <Loader2 className="h-8 w-8 animate-spin" style={{ color: COLORS.ink }} />
      <span className="ml-3" style={{ color: COLORS.muted, fontWeight: 700 }}>
        {error || "課題説明へ移動しています..."}
      </span>
    </div>
  );
}
